import { HttpClient } from '@angular/common/http';
import { EventEmitter, Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, lastValueFrom, throwError } from 'rxjs';
import { app } from '../../environments/app';
import { environment } from '../../environments/environment';
import { LoginInfo } from '../models/login-info';
import { ModuleUpdateStatus } from '../models/module-update-status';
import { PmConfig } from '../models/product/pm-config';
import { PosConfig } from '../models/pos/pos-config';
import { SaleConfig } from '../models/sale/sale-config';
import { PreparationConfig } from '../models/preparation/preparation-config';
import { CatalogConfig } from '../models/product/catalog-config';
import { PurchaseConfig } from '../models/purchase/purchase-config';
import { EInvoicingConfig } from '../models/einvoicing/einvoicing-config';
import { ServiceConfig } from '../models/service/service-config';
import { SubscriptionConfig } from '../models/subscription/subscription-config';
import { DonationConfig } from '../models/donation/donation-config';
import { ConsignmentConfig } from '../models/consignment/consignment-config';
import { WorkshopConfig } from '../models/workshop/workshop-config';
import { SessionInfo } from '../models/session-info';
import { ShopConfig } from '../models/shop-config';
import { CustomerConfig } from '../models/customer/customer-config';
import { TranslateService } from '@ngx-translate/core';
import { isPlatformBrowser } from '@angular/common';
import { ForgotPasswordRequest } from '../models/forgot-password-request';
import { EmailInfo } from '../models/email/email-info';
import { ResetPasswordRequest } from '../models/reset-password-request';

@Injectable({
  providedIn: 'root',
})
export class PositizeService {
  shopConfig: ShopConfig | null = null;
  customerConfig: CustomerConfig | null = null;
  catalogConfig: CatalogConfig | null = null;
  sessionInfo: SessionInfo | null = null;
  activeModule: string | null = null;
  redirectLoginError: any = null;

  get requestedShop(): string | null {
    return sessionStorage.getItem('poz_requested_shop');
  }
  set requestedShop(value: string | null) {
    if (value) sessionStorage.setItem('poz_requested_shop', value);
    else sessionStorage.removeItem('poz_requested_shop');
  }
  isMobile: boolean = false;
  language: string = 'en';  

  public session: any = {};

  activeMenuChange: EventEmitter<any> = new EventEmitter<any>();
  onLoginChange: EventEmitter<any> = new EventEmitter<any>();
  onIsMobileChange: EventEmitter<any> = new EventEmitter<any>();

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private router: Router,
    private httpClient: HttpClient,
    private translate: TranslateService
  ) {}

  setIsMobile(isMobile: boolean) {
    this.isMobile = isMobile;
    this.onIsMobileChange.emit(isMobile);
  }

  async checkGatewayToken(): Promise<boolean> {
    return lastValueFrom(this.get('positize/checkGatewayToken'));
  }

  async resetGatewayToken() {
    return lastValueFrom(this.post('positize/resetGatewayToken', null));
  }

  async hasModule(module: string): Promise<boolean> {
    if (this.sessionInfo == null) {
      return false;
    }
    return this.sessionInfo.shopModules.includes(module);
  }

  getShops(): string[] {
    let item = localStorage.getItem('poz_shops');
    if (item == null) {
      return [];
    }
    return JSON.parse(item);
  }

  addShop(shopName: string) {
    let shops: string[] = this.getShops();
    let index = shops.indexOf(shopName);
    if (index != -1) {
      return;
    }
    shops.push(shopName);
    localStorage.setItem('poz_shops', JSON.stringify(shops));
  }

  removeShop(shopName: string) {
    let shops: string[] = this.getShops();
    let index = shops.indexOf(shopName);
    if (index != -1) {
      shops.splice(index, 1);
      localStorage.setItem('poz_shops', JSON.stringify(shops));
    }
  }

  getVersion(): string {
    return app.version;
  }

  async logout(navigate = true) {
    try {
      await lastValueFrom(this.post('positize/logout', null));
    } catch (error) {
      console.log('logout error=' + JSON.stringify(error));
    }
    this.sessionInfo = null;
    this.shopConfig = null;
    this.customerConfig = null;
    this.catalogConfig = null;
    this.onLoginChange.emit(false);
    if (navigate) {
      this.navigateTo('/login');
    }
  }

  async getPreparationConfig() {
    let config = await lastValueFrom(this.getParameter('preparation_config'));

    if (!config) {
      config = new PreparationConfig();
    }

    return config;
  }

  async getSmtpInfo() {
    let smtpInfo = await lastValueFrom(this.getParameter('smtp_info'));

    if (!smtpInfo) {
      smtpInfo = {
        hostname: '',
        port: 465,
        username: '',
        password: '',
        security: true,
      };
    }

    return smtpInfo;
  }

  async saveSmtpInfo(smtpInfo: any) {
    return await lastValueFrom(this.saveParameter('smtp_info', smtpInfo));
  }

  async getPurchaseConfig(): Promise<PurchaseConfig> {
    let config = await lastValueFrom(this.getParameter('purchase_config'));

    if (!config) {
      config = new PurchaseConfig();
    }

    return config;
  }

  async savePurchaseConfig(config: PurchaseConfig) {
    return await lastValueFrom(this.saveParameter('purchase_config', config));
  }

  async getEInvoicingConfig(): Promise<EInvoicingConfig> {
    const config = await lastValueFrom(this.getParameter('einvoicing_config'));

    return new EInvoicingConfig(config || {});
  }

  async saveEInvoicingConfig(config: EInvoicingConfig): Promise<EInvoicingConfig> {
    const saved = await lastValueFrom(this.saveParameter('einvoicing_config', config));
    return new EInvoicingConfig(saved || config);
  }

  async getServiceConfig(): Promise<ServiceConfig> {
    let config = await lastValueFrom(this.getParameter('service_config'));

    // Merge with default config to ensure all properties exist
    const defaultConfig = new ServiceConfig();
    return Object.assign(defaultConfig, config || {});
  }

  async saveServiceConfig(config: ServiceConfig) {
    return await lastValueFrom(this.saveParameter('service_config', config));
  }

  async getSubscriptionConfig(): Promise<SubscriptionConfig> {
    let config = await lastValueFrom(this.getParameter('subscription_config'));

    // Merge with default config to ensure all properties exist
    const defaultConfig = new SubscriptionConfig();
    return Object.assign(defaultConfig, config || {});
  }

  async saveSubscriptionConfig(config: SubscriptionConfig) {
    return await lastValueFrom(this.saveParameter('subscription_config', config));
  }

  async getDonationConfig(): Promise<DonationConfig> {
    let config = await lastValueFrom(this.getParameter('donation_config'));
    const defaultConfig = new DonationConfig();
    return Object.assign(defaultConfig, config || {});
  }

  async saveDonationConfig(config: DonationConfig): Promise<DonationConfig> {
    const saved = await lastValueFrom(
      this.saveParameter('donation_config', config)
    );
    return Object.assign(new DonationConfig(), saved || config);
  }

  async getConsignmentConfig(): Promise<ConsignmentConfig> {
    let config = await lastValueFrom(this.getParameter('consignment_config'));
    const defaultConfig = new ConsignmentConfig();
    return Object.assign(defaultConfig, config || {});
  }

  async saveConsignmentConfig(
    config: ConsignmentConfig
  ): Promise<ConsignmentConfig> {
    const saved = await lastValueFrom(
      this.saveParameter('consignment_config', config)
    );
    return Object.assign(new ConsignmentConfig(), saved || config);
  }

  async getWorkshopConfig(): Promise<WorkshopConfig> {
    let config = await lastValueFrom(this.getParameter('workshop_config'));
    const defaultConfig = new WorkshopConfig();
    return Object.assign(defaultConfig, config || {});
  }

  async saveWorkshopConfig(config: WorkshopConfig): Promise<WorkshopConfig> {
    const saved = await lastValueFrom(
      this.saveParameter('workshop_config', config)
    );
    return Object.assign(new WorkshopConfig(), saved || config);
  }

  async getCatalogConfig(refresh: boolean = false): Promise<CatalogConfig> {
    if (!this.catalogConfig || refresh) {
      const raw = await lastValueFrom(this.get<any>('positize/catalogConfig'));
      this.catalogConfig = Object.assign(new CatalogConfig(), raw || {});
    }
    return this.catalogConfig;
  }

  async saveCatalogConfig(config: CatalogConfig): Promise<CatalogConfig> {
    const saved = await lastValueFrom(
      this.post<any>('positize/catalogConfig', config)
    );
    this.catalogConfig = Object.assign(new CatalogConfig(), saved || config);
    return this.catalogConfig;
  }

  async getCustomerConfig(refresh: boolean = false): Promise<CustomerConfig> {
    if (!this.customerConfig || refresh) {
      const raw = await lastValueFrom(this.getParameter('customer_config'));
      this.customerConfig = Object.assign(new CustomerConfig(), raw || {});
    }
    return this.customerConfig;
  }

  async saveCustomerConfig(config: CustomerConfig): Promise<CustomerConfig> {
    const saved = await lastValueFrom(
      this.saveParameter('customer_config', config)
    );
    this.customerConfig = Object.assign(new CustomerConfig(), saved || config);
    return this.customerConfig;
  }

  checkEmail(email: string) {
    if (email) {
      return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(email);
    }
    return false;
  }

  isExternalHandler(): boolean {
    return this.sessionInfo?.externalHandler ?? false;
  }

  async getPosConfig(): Promise<PosConfig> {
    let posConfig = await lastValueFrom(this.getParameter('pos_config'));

    if (!posConfig) {
      posConfig = new PosConfig();
    }
    return posConfig;
  }

  async getSaleConfig(): Promise<SaleConfig> {
    let config = await lastValueFrom(this.getParameter('sale_config'));

    if (!config) {
      config = new SaleConfig();
    }
    return config;
  }

  async saveSaleConfig(config: SaleConfig) {
    return await lastValueFrom(this.saveParameter('sale_config', config));
  }

async getPmConfig() {
    let pmConfig = await lastValueFrom(this.getParameter('pm_config'));

    if (!pmConfig) {
      pmConfig = new PmConfig();
    }
    return pmConfig;
  }

  now() {
    return Date.now();
  }
  shopKey(sessionInfo: any) {
    return sessionInfo?.shopName ?? '_unknown';
  }

  private ensureBrowser(): void {
    if (!isPlatformBrowser(this.platformId)) {
      throw new Error('localStorage indisponible côté serveur.');
    }
  }

  getLocal<T = unknown>(name: string, defaultValue: T | null = null): T | null {
    this.ensureBrowser();
    const shop = this.shopKey(this.sessionInfo);
    const key = `${shop}:${name}`;

    try {
      const item = localStorage.getItem(key);
      if (item === null) {
        return defaultValue;
      }
      return JSON.parse(item) as T;
    } catch (error) {
      console.error('getLocal error:', error);
      return defaultValue;
    }
  }

  saveLocal(name: string, value: unknown): void {
    this.ensureBrowser();
    if (!this.sessionInfo) {
      console.log('saveLocal error: sessionInfo is null');
      return;
    }
    const shop = this.shopKey(this.sessionInfo);
    const key = `${shop}:${name}`;

    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error('saveLocal error:', error);
      throw error;
    }
  }

  removeLocal(name: string): void {
    this.ensureBrowser();
    const shop = this.shopKey(this.sessionInfo);
    const key = `${shop}:${name}`;
    localStorage.removeItem(key);
  }

  async refreshSessionInfo() {
    this.sessionInfo = await lastValueFrom(this.checkSession());
  }

  async initializeSession() {
    try {
      this.sessionInfo = await lastValueFrom(this.checkSession());
    } catch (error) {
      console.log('no session found');
      this.sessionInfo = null;
    }

    if (this.sessionInfo) {
      try {
        this.shopConfig = await this.getShopConfig(true);
        this.customerConfig = await this.getCustomerConfig(true);
        this.catalogConfig = await this.getCatalogConfig(true);
        this.activeModule = this.getLocal('active_module', null);
      } catch (error) {
        // Session is valid but shop-scoped data can't be loaded — likely a
        // stale session pointing at a deleted/broken shop. Treat as logged out
        // so the caller can route to /login instead of crashing.
        console.log('session points to a stale shop, clearing', error);
        this.sessionInfo = null;
        this.shopConfig = null;
        this.customerConfig = null;
        this.catalogConfig = null;
        try { await this.logout(false); } catch {}
      }
    }
  }

  navigateTo(path: any) {
    //check path is array
    if (Array.isArray(path)) {
      this.router.navigate(path);
    } else {
      this.router.navigate([path]);
    }
  }

  async canActivate(checkAdmin: boolean = false) {
    if (this.sessionInfo == null) {
      await this.initializeSession();
    }

    if (this.sessionInfo == null) {
      this.router.navigate(['/login']);
      return false;
    }

    if (this.sessionInfo.maintenanceMode || this.sessionInfo.coreMaintenanceMode) {
      this.router.navigate(['/maintenance']);
      return false;
    }

    if (checkAdmin && !this.sessionInfo.admin) {
      this.router.navigate(['/access-denied']);
      return false;
    }

    return true;
  }

  isLogged() {
    return this.sessionInfo != null;
  }

  canMatch(): boolean {
    return true;
  }

  checkSession(): Observable<SessionInfo> {
    return this.get<SessionInfo>('positize/checkSession');
  }

  catchAccessForbidden(error: any) {
    if (error.status == 503 && error.error?.maintenance) {
      this.navigateTo('/maintenance');
      return;
    }
    if (error.status == 401 && this.sessionInfo) {
      this.redirectLoginError = error;
      this.sessionInfo = null;
      this.shopConfig = null;
      this.customerConfig = null;
      this.onLoginChange.emit(false);
      this.navigateTo('/login');
    }
  }

  get<T>(endpoint: string): Observable<T> {
    return this.httpClient
      .get<T>(environment.positizeHubUrl + '/' + endpoint, {
        withCredentials: true,
      })
      .pipe(
        catchError((error) => {
          this.catchAccessForbidden(error);
          throw error;
        })
      );
  }

  getString(endpoint: string): Observable<string> {
    return this.httpClient
      .get(environment.positizeHubUrl + '/' + endpoint, {
        withCredentials: true,
        responseType: 'text',
      })
      .pipe(
        catchError((error) => {
          this.catchAccessForbidden(error);
          throw error;
        })
      );
  }

  getBlob(endpoint: string): Observable<any> {
    return this.httpClient
      .get(environment.positizeHubUrl + '/' + endpoint, {
        withCredentials: true,
        responseType: 'blob',
      })
      .pipe(
        catchError((error) => {
          this.catchAccessForbidden(error);
          throw error;
        })
      );
  }

  postString(endpoint: string, data: any): Observable<string> {
    return this.httpClient
      .post(environment.positizeHubUrl + '/' + endpoint, data, {
        withCredentials: true,
        responseType: 'text',
      })
      .pipe(
        catchError((error) => {
          this.catchAccessForbidden(error);
          throw error;
        })
      );
  }

  post<T>(endpoint: string, data: any): Observable<T> {
    return this.httpClient
      .post<T>(environment.positizeHubUrl + '/' + endpoint, data, {
        withCredentials: true,
      })
      .pipe(
        catchError((error) => {
          this.catchAccessForbidden(error);
          throw error;
        })
      );
  }

  put<T>(endpoint: string, data: any): Observable<T> {
    return this.httpClient
      .put<T>(environment.positizeHubUrl + '/' + endpoint, data, {
        withCredentials: true,
      })
      .pipe(
        catchError((error) => {
          this.catchAccessForbidden(error);
          throw error;
        })
      );
  }

  patch<T>(endpoint: string, data: any): Observable<T> {
    return this.httpClient
      .patch<T>(environment.positizeHubUrl + '/' + endpoint, data, {
        withCredentials: true,
      })
      .pipe(
        catchError((error) => {
          this.catchAccessForbidden(error);
          throw error;
        })
      );
  }

  postFormData<T>(endpoint: string, formData: FormData): Observable<T> {
    return this.httpClient
      .post<T>(environment.positizeHubUrl + '/' + endpoint, formData, {
        withCredentials: true,
      })
      .pipe(
        catchError((error) => {
          this.catchAccessForbidden(error);
          throw error;
        })
      );
  }

  putFormData<T>(endpoint: string, formData: FormData): Observable<T> {
    return this.httpClient
      .put<T>(environment.positizeHubUrl + '/' + endpoint, formData, {
        withCredentials: true,
      })
      .pipe(
        catchError((error) => {
          this.catchAccessForbidden(error);
          throw error;
        })
      );
  }

  delete(endpoint: string) {
    return this.httpClient
      .delete(environment.positizeHubUrl + '/' + endpoint, {
        withCredentials: true,
      })
      .pipe(
        catchError((error) => {
          this.catchAccessForbidden(error);
          throw error;
        })
      );
  }

  getList<T>(endpoint: string) {
    return this.httpClient
      .get<T[]>(environment.positizeHubUrl + '/' + endpoint, {
        withCredentials: true,
      })
      .pipe(
        catchError((error) => {
          this.catchAccessForbidden(error);
          throw error;
        })
      );
  }

  getParameter(parameterName: string): Observable<any> {
    return this.get<any>('positize/parameter/' + parameterName).pipe(
      catchError((error) => {
        this.catchAccessForbidden(error);
        throw error;
      })
    );
  }

  saveParameter(parameterName: string, parameterValue: any): Observable<any> {
    return this.post<any>(
      'positize/parameter/' + parameterName,
      parameterValue
    ).pipe(
      catchError((error) => {
        this.catchAccessForbidden(error);
        throw error;
      })
    );
  }

  getUserPreference(key: string): Observable<any> {
    return this.get<any>('positize/user-preference/' + key);
  }

  getDashboardNews(): Observable<any[]> {
    return this.get<any[]>('positize/dashboard-news');
  }

  getShopNews(): Observable<any[]> {
    return this.get<any[]>('positize/shop-news');
  }

  saveShopNews(news: any): Observable<any> {
    return this.post<any>('positize/shop-news', news);
  }

  deleteShopNews(id: number): Observable<any> {
    return this.delete('positize/shop-news/' + id);
  }

  saveUserPreference(key: string, value: any): Observable<any> {
    return this.post<any>('positize/user-preference/' + key, value);
  }

  deleteUserPreference(key: string): Observable<any> {
    return this.delete('positize/user-preference/' + key);
  }

  login(
    shopname: string,
    username: string,
    password: string
  ): Observable<SessionInfo> {
    if (shopname && username && password) {
      return this.post<SessionInfo>(
        'positize/login',
        new LoginInfo(shopname, username, password)
      );
    } else {
      return throwError(() => new Error('Invalid shopname|username|password'));
    }
  }

  async getShopConfig(refresh: boolean = false): Promise<ShopConfig> {
    if (!this.shopConfig || refresh) {
      this.shopConfig = await lastValueFrom(
        this.get<ShopConfig>('positize/shopConfig')
      );
    }
    return this.shopConfig;
  }

  async updateGatewayModule(): Promise<ModuleUpdateStatus> {
    return await lastValueFrom(this.get('positize/updateGatewayModule'));
  }

  saveShopConfig(shopConfig: ShopConfig): Observable<ShopConfig> {
    return this.post<ShopConfig>('positize/shopConfig', shopConfig);
  }

  getShopLogoUrl(): string {
    return `${environment.positizeHubUrl}/positize/shopConfig/logo`;
  }

  uploadShopLogo(file: File): Observable<ShopConfig> {
    const formData = new FormData();
    formData.append('file', file);
    return this.postFormData<ShopConfig>('positize/shopConfig/logo', formData);
  }

  deleteShopLogo() {
    return this.delete('positize/shopConfig/logo');
  }

  public formatDate(date: any): string {
    if (!date) return '';
    return new Date(date).toLocaleString(
      this.translate.getCurrentLang() || 'fr'
    );
  }

  public formatCurrency(amount: number): string {
    if (amount === null || amount === undefined) return '0';
    return new Intl.NumberFormat(this.translate.getCurrentLang() || 'fr', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  }

  public async isExternalShop(shopName: string): Promise<boolean> {
    return await lastValueFrom(this.get('positize/isExternalShop/' + shopName));
  }

  public async forgotPassword(
    forgotPasswordRequest: ForgotPasswordRequest
  ): Promise<boolean> {
    return await lastValueFrom(
      this.post('positize/forgotPassword', forgotPasswordRequest)
    );
  }

  public async resetPassword(
    resetPasswordRequest: ResetPasswordRequest
  ): Promise<boolean> {
    return await lastValueFrom(
      this.post('positize/resetPassword', resetPasswordRequest)
    );
  }

  async sendTestEmail(emailInfo: EmailInfo) {
    return await lastValueFrom(this.post('positize/sendTestEmail', emailInfo));
  }

  async sendEmail(emailInfo: EmailInfo) {
    return await lastValueFrom(this.post('positize/sendEmail', emailInfo));
  }
}
